import TooltipLink from "@/components/TooltipLink/TooltipLink";
import useTranslation from "@/hooks/useTranslation";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Popconfirm, Tooltip, message } from "antd";
import { ReactNode } from "react";
import { useDeleteProducts } from "../../useProducts";

export default function DeleteProductAction({
  children,
  products,
  onSucces,
  deselectUndeletableProducts,
}: {
  children: ReactNode;
  products: { deletable: boolean; id: number }[];
  onSucces?: () => void;
  deselectUndeletableProducts?: () => void;
}) {
  const deleteProducts = useDeleteProducts();

  const productText =
    products.length === 1 ? "product" : `${products.length} product${products.length > 1 && "s"}`;

  const disabled = products.some((x) => !x.deletable);
  const undeletableProductCount = products.filter((x) => !x.deletable).length;
  const translation = useTranslation();

  return (
    <Popconfirm
      title={`Delete ${productText}`}
      description={
        <>
          <p>Deleting a product also deletes:</p>
          <ul>
            <li>the BOM of this product</li>
            <li>the BOM of proxy products that use this product as a source</li>
          </ul>
          <p style={{ color: "#F5222D" }}>This action can not be undone.</p>
        </>
      }
      onConfirm={() =>
        deleteProducts
          .mutateAsync({ productIds: products.map((x) => x.id) })
          .then((result) => {
            if (result.status === 200) {
              message.success(`Product${products.length > 1 ? "s" : ""} removed.`);
              onSucces && onSucces();
            }
          })
          .catch(() => message.error(translation.errors.general))
      }
      onCancel={() => {}}
      icon={<ExclamationCircleFilled style={{ color: "#F5222D" }} />}
      okText={`Delete ${productText}`}
      cancelText="Cancel"
      placement="bottom"
      okType="danger"
      okButtonProps={{ loading: deleteProducts.isLoading }}
      disabled={disabled}
    >
      <Tooltip
        placement="bottom"
        title={
          disabled &&
          (products.length === 1 ? (
            <p>
              This product can not be deleted as it is referenced in one of your purchase or sales
              orders. Remove the purchase and sales orders referencing this product to remove this
              product.
            </p>
          ) : (
            <p>
              {undeletableProductCount} of the products you selected are referenced in a purchase or
              sales order.{" "}
              {deselectUndeletableProducts ? (
                <>
                  <TooltipLink
                    onClick={() => deselectUndeletableProducts()}
                    to={""}
                  >
                    Click here
                  </TooltipLink>{" "}
                  to deselect them or remove the related purchase and sales orders to continue.
                </>
              ) : (
                <>Remove the related purchase and sales orders to continue.</>
              )}
            </p>
          ))
        }
      >
        {children}
      </Tooltip>
    </Popconfirm>
  );
}
